import React, { useState } from "react"
import Switch from "react-switch"
import { Link } from "react-router-dom"
import Select from "react-select"
import moment from "moment"
import { DragDropContext, Droppable } from "react-beautiful-dnd"
import * as HoverCard from "@radix-ui/react-hover-card"
import { Modal, ModalBody, ModalHeader } from "reactstrap"

import awicon from "../../../assets/images/services-icon/coin.png"
import st2 from "../../../assets/images/services-icon/st2.png"
import chevleft from "../../../assets/images/services-icon/chev-left.png"
import { ActivityType } from "../../../modules/lesson/activity/activity.types"
import { getMomentLocaleTimeFormat } from "../../../common/utils/date-utils"
import { ContextMenuDropdown } from "../../../components/ContextMenuDropdown"

import { userSetting } from "./PlanLessonHelpers"
import { usePlanLessonLocal } from "./PlanLessonContext"
import {
  LocalMathOnlineCard,
  LocalEnglishOnlineCard,
  LocalGeneralActivityCard,
  LocalLllActivityCard,
  LocalGameCard,
  DraggableCard,
} from "./PlanLessonCards"
import { LocalDiagnosticsActivityCard } from "./PlanLessonDiagnostics"
import { LocalBmdActivityCard } from "./PlanLessonBmd"
import { LocalSkillbuilderActivityCard } from "./PlanLessonSkillbuilder"
import { SnapshotIcon } from "./ai-lesson-planning/ChildSnapshot"
import type {
  ContextMenuOption,
  PlanLessonActivity,
  PlanLessonHeaderSectionProps,
  PlanningSectionProps,
  PreNextSessionSectionProps,
  SelectOption,
} from "./PlanLessonTypes"

const ONLINE_ACTIVITY_TYPES = [0, 8]
const GENERAL_ACTIVITY_TYPES = [1, 3, 4, 5, 10, ActivityType.ONETIME]

const compactSessionSelectStyles = {
  container: (base: any) => ({
    ...base,
    width: "100%",
    minWidth: 0,
  }),
  control: (base: any) => ({
    ...base,
    minHeight: "28px",
  }),
  valueContainer: (base: any) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  singleValue: (base: any) => ({
    ...base,
    fontSize: "11px",
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
}

const compactStudentSelectStyles = {
  container: (base: any) => ({
    ...base,
    width: "100%",
    minWidth: 0,
  }),
  control: (base: any) => ({
    ...base,
    minHeight: "28px",
  }),
  valueContainer: (base: any) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  singleValue: (base: any) => ({
    ...base,
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: 1.15,
    flex: "0 1 auto",
    fontSize: "11px",
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: 9999,
  }),
}

const planningHeaderMiniSwitchWrapStyle: React.CSSProperties = {
  transform: "scale(0.9)",
  transformOrigin: "left center",
}

function getActivityType(activity: PlanLessonActivity): number {
  return Number(activity.activity_type)
}

function getSubjectId(activity: PlanLessonActivity): number {
  return Number(activity.fk_sub_id)
}

function isOnlineActivity(activity: PlanLessonActivity): boolean {
  return ONLINE_ACTIVITY_TYPES.includes(getActivityType(activity))
}

function isMathOnlineActivity(activity: PlanLessonActivity): boolean {
  return isOnlineActivity(activity) && getSubjectId(activity) === 2
}

function isEnglishOnlineActivity(activity: PlanLessonActivity): boolean {
  return isOnlineActivity(activity) && getSubjectId(activity) === 1
}

function isDiagnosticsActivity(activity: PlanLessonActivity): boolean {
  return (
    getActivityType(activity) === 2 && [1, 2].includes(getSubjectId(activity))
  )
}

function isGeneralActivity(activity: PlanLessonActivity): boolean {
  const activityType = getActivityType(activity)
  const subjectId = getSubjectId(activity)

  return (
    GENERAL_ACTIVITY_TYPES.includes(activityType) ||
    ([6, 7].includes(activityType) && [1, 2].includes(subjectId))
  )
}

function isGameActivity(activity: PlanLessonActivity): boolean {
  return getActivityType(activity) === ActivityType.PLATFORM_GAMES
}

function isLllActivity(activity: PlanLessonActivity): boolean {
  return getActivityType(activity) === ActivityType.LLL
}

/**
 * Math BMD, English diagnostics and English Skill Builders share one card.
 *
 * The Skill Builder belongs here and NOT with isSkillbuilderActivity below, which is the maths
 * PDF worksheet: that card is built around a worksheet and its preview, while this one draws a
 * question-count activity with a flow_status and a Mark answers button. Without this line a
 * Skill Builder falls through to `return null` and is INVISIBLE on the lesson plan while the
 * picker reports it as assigned - the exact bug the comment on the worksheet branch records.
 */
function isBmdActivity(activity: PlanLessonActivity): boolean {
  const type = getActivityType(activity)
  return (
    type === ActivityType.DIAGNOSTICS_FLOW ||
    type === ActivityType.ENGLISH_DIAGNOSTICS ||
    type === ActivityType.ENGLISH_SKILLBUILDER
  )
}

function isSkillbuilderActivity(activity: PlanLessonActivity): boolean {
  return getActivityType(activity) === ActivityType.SKILLBUILDER
}

function hasRenderableEnrolment(enrolment: any): boolean {
  const attendanceStatus = enrolment?.tbl_session_attendance?.attendance_status;
  const is_paused = enrolment?.tbl_session_attendance?.is_paused;

  return !!(enrolment?.tbl_student && (attendanceStatus != 6 || is_paused == 1))
}

function getEnrolmentActivities(
  enrolment: any,
  isHomework: boolean
): PlanLessonActivity[] {
  return (enrolment?.tbl_student?.Lesson?.LessonActivities || []).filter(
    (activity: PlanLessonActivity) =>
      parseInt(String(activity.marked_for_homework), 10) ===
      (isHomework ? 1 : 0)
  )
}

function formatSessionDate(sessionDate: any, format: string): string | null {
  return sessionDate
    ? moment(sessionDate, moment.ISO_8601).format(format)
    : null
}


interface SessionNavButtonProps {
  show: boolean
  onClick: () => void
  iconClassName: string
}

const SessionNavButton = ({
  show,
  onClick,
  iconClassName,
}: SessionNavButtonProps) => {
  if (!show) return null

  return (
    <button
      type="button"
      className="btn btn-sm bg-white border rounded-circle d-flex align-items-center justify-content-center"
      style={{
        width: "26px",
        height: "26px",
        padding: 0,
        lineHeight: 1,
      }}
      onClick={onClick}
    >
      <i className={iconClassName}></i>
    </button>
  )
}

interface RenderActivityCardParams {
  activity: PlanLessonActivity
  isHomework: boolean
  sessionData: any
  index: number
  isPlanningSession: boolean
  enrolment?: any
}

function renderActivityCard({
  activity,
  isHomework,
  sessionData,
  index,
  isPlanningSession,
  enrolment,
}: RenderActivityCardParams): React.ReactNode {
  if (isMathOnlineActivity(activity)) {
    return (
      <LocalMathOnlineCard
        activity={activity}
        isHomework={isHomework}
        isPlanningSession={isPlanningSession}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
      />
    )
  }

  if (isEnglishOnlineActivity(activity)) {
    return (
      <LocalEnglishOnlineCard
        activity={activity}
        isHomework={isHomework}
        isPlanningSession={isPlanningSession}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
      />
    )
  }

  if (isGeneralActivity(activity)) {
    return (
      <LocalGeneralActivityCard
        activity={activity}
        isHomework={isHomework}
        isPlanningSession={isPlanningSession}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
      />
    )
  }

  if (isDiagnosticsActivity(activity)) {
    return (
      <LocalDiagnosticsActivityCard
        isPlanningSession={isPlanningSession}
        isHomework={isHomework}
        activity={activity as any}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
      />
    )
  }

  if (isGameActivity(activity)) {
    return (
      <LocalGameCard
        isPlanningSession={isPlanningSession}
        activity={activity}
        sessionData={sessionData}
        enrolment={enrolment}
        index={index}
      />
    )
  }

  if (isLllActivity(activity)) {
    return (
      <LocalLllActivityCard
        isPlanningSession={isPlanningSession}
        activity={activity}
        sessionData={sessionData}
        enrolment={enrolment}
        index={index}
      />
    )
  }

  if (isBmdActivity(activity)) {
    return (
      <LocalBmdActivityCard
        isPlanningSession={isPlanningSession}
        isHomework={isHomework}
        activity={activity}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
      />
    )
  }

  // Without this the chain falls through to `return null` below and a Skill
  // Builder is INVISIBLE on the lesson plan while the picker reports it as
  // assigned, which reads as the assignment having failed.
  if (isSkillbuilderActivity(activity)) {
    return (
      <LocalSkillbuilderActivityCard
        isPlanningSession={isPlanningSession}
        isHomework={isHomework}
        activity={activity}
        sessionData={sessionData}
        index={index}
        enrolment={enrolment}
      />
    )
  }

  return null
}

interface PlanningActivityCardProps {
  activity: PlanLessonActivity
  dragDisabled?: boolean
  isHomework: boolean
  sessionData: any
  index: number
}

const PlanningActivityCard = ({
  activity,
  dragDisabled,
  isHomework,
  sessionData,
  index,
}: PlanningActivityCardProps) => {
  const content = renderActivityCard({
    activity,
    isHomework,
    sessionData,
    index,
    isPlanningSession: true,
  })

  if (!content) return null

  return (
    <DraggableCard
      activity={activity}
      disabled={dragDisabled}
      isHomework={isHomework}
      activityIndex={index}
    >
      {content}
    </DraggableCard>
  )
}

interface SessionActivityCardProps {
  activity: PlanLessonActivity
  isHomework: boolean
  sessionData: any
  index: number
  enrolment: any
}

const SessionActivityCard = ({
  activity,
  isHomework,
  sessionData,
  index,
  enrolment,
}: SessionActivityCardProps) => {
  return renderActivityCard({
    activity,
    isHomework,
    sessionData,
    index,
    isPlanningSession: false,
    enrolment,
  })
}

interface AttendanceStatusBadgeProps {
  attendanceStatus?: number | null
}

const AttendanceStatusBadge = ({
  attendanceStatus,
}: AttendanceStatusBadgeProps) => {
  const badgeMap: Record<number, { text: string; className: string }> = {
    0: {
      text: "pending",
      className: "badge tb-primary font-size-10 text-uppercase cp-bdg",
    },
    1: {
      text: "Ongoing",
      className: "badge tb-warning font-size-10 text-uppercase cp-bdg",
    },
    2: {
      text: "Attended",
      className: "badge tb-success font-size-10 text-uppercase cp-bdg",
    },
    3: {
      text: "Missed",
      className: "badge tb-danger font-size-10 text-uppercase cp-bdg",
    },
    5: {
      text: "Deferment Requested",
      className: "badge tb-warning font-size-10 text-uppercase cp-bdg",
    },
    6: {
      text: "Cancelled",
      className: "badge tb-danger font-size-10 text-uppercase cp-bdg",
    },
    7: {
      text: "Paused",
      className: "badge tb-danger font-size-10 text-uppercase cp-bdg",
    },
  }

  const badge = attendanceStatus != null ? badgeMap[attendanceStatus] : null
  if (!badge) return null

  return <span className={badge.className}>{badge.text}</span>
}

interface PreNextSessionHeaderProps {
  preNextType: number
  currentPreviousSessioncount: number
  previousSessioncount: number
  triggerPrevious: () => void
  triggerNextPrevious: () => void
  triggerNext: () => void
  sessionPreNextData: any
  switchFlexi: boolean
  onToggleFlexi: (checked: boolean) => void
  Offsymbol: React.ComponentType<any>
  OnSymbol: React.ComponentType<any>
}

const PreNextSessionHeader = ({
  preNextType,
  currentPreviousSessioncount,
  previousSessioncount,
  triggerPrevious,
  triggerNextPrevious,
  triggerNext,
  sessionPreNextData,
  switchFlexi,
  onToggleFlexi,
  Offsymbol,
  OnSymbol,
}: PreNextSessionHeaderProps) => {
  const session = sessionPreNextData?.session
  const timezone = localStorage.getItem("timezone") || "Europe/London"
  const flexiDays = parseInt(localStorage.getItem("flexiDays") || "0", 10)

  const showFlexiSwitch =
    !!session?.ses_date &&
    moment(session.ses_date)
      .tz(timezone)
      .add(flexiDays + 1, "days") >= moment().tz(timezone) &&
    moment(session.ses_date).tz(timezone) <= moment().tz(timezone)

  return (
    <div
      className="ss-details border-bottom1 bg-white px-2 pt-1 pb-2 mb-0 state-bg"
      style={{ backgroundColor: "#fafbfd" }}
    >
      {preNextType ? (
        <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap mb-1">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h5 className="mb-0 text-dark fw-700" style={{ fontSize: "12px" }}>
              {preNextType === 1
                ? "Past session"
                : preNextType === 2
                  ? "Next session"
                  : null}
            </h5>

            {preNextType === 1 ? (
              <span className="bc-bg2 px-2 py-1 rounded-pill text-center">
                <strong>{currentPreviousSessioncount}</strong>
              </span>
            ) : null}
          </div>

          <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
            <SessionNavButton
              show={preNextType === 2 || previousSessioncount > 0}
              onClick={triggerPrevious}
              iconClassName="mdi mdi-chevron-left"
            />

            <div
              className="text-dark text-center"
              style={{ fontSize: "11px", fontWeight: 600, minWidth: "100px" }}
            >
              {formatSessionDate(session?.ses_date, "DD MMM YYYY") || "No data"}
            </div>

            <SessionNavButton
              show={currentPreviousSessioncount > 1}
              onClick={triggerNextPrevious}
              iconClassName="mdi mdi-chevron-right"
            />

            <SessionNavButton
              show={preNextType === 1 && currentPreviousSessioncount === 1}
              onClick={triggerNext}
              iconClassName="mdi mdi-chevron-right"
            />

            <AttendanceStatusBadge
              attendanceStatus={
                session?.tbl_session_time?.tbl_student_enrolment?.[0]
                  ?.tbl_session_attendance?.is_paused == '1' ?
                  '7'
                  :
                  session?.tbl_session_time?.tbl_student_enrolment?.[0]
                    ?.tbl_session_attendance?.attendance_status

              }
            />
          </div>
        </div>
      ) : null}

      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
        <div style={{ minWidth: 0 }}>
          <div
            className="text-dark fw-700 d-flex align-items-center flex-wrap gap-2"
            style={{ fontSize: "11px", lineHeight: 1.2 }}
          >
            <span>{session?.tbl_session_time?.sest_name || "-"}</span>
            {session?.is_flexi == 1 ? (
              <span className="badge tb-danger font-size-10 text-uppercase cp-bdg">
                Flexi
              </span>
            ) : null}
          </div>

          <div
            className="text-muted"
            style={{ fontSize: "10px", lineHeight: 1.2 }}
          >
            {session?.sub_name || "-"}
          </div>

          <div
            className="text-muted"
            style={{ fontSize: "10px", lineHeight: 1.2 }}
          >
            {session?.tbl_session_time?.sest_type || "-"}
          </div>
        </div>

        <div className="d-flex align-items-start gap-3 ms-auto flex-wrap">
          {showFlexiSwitch ? (
            <div className="d-flex align-items-center gap-2">
              <span
                className="text-dark"
                style={{ fontSize: "10px", fontWeight: 600 }}
              >
                Flexi
              </span>
              <div className="switch-wrapper">
                <div className="sw-child">
                  <Switch
                    uncheckedIcon={<Offsymbol />}
                    checkedIcon={<OnSymbol />}
                    onColor="#ec4561"
                    onChange={onToggleFlexi}
                    checked={switchFlexi}
                  />
                </div>
              </div>
            </div>
          ) : null}

          <div className="text-end">
            <div
              className="text-dark fw-700"
              style={{ fontSize: "10px", lineHeight: 1.2 }}
            >
              {session?.tut_fname
                ? `${session.tut_fname} ${session.tut_surname}`
                : "-"}
            </div>

            <div
              className="text-muted"
              style={{ fontSize: "10px", lineHeight: 1.2 }}
            >
              {formatSessionDate(session?.ses_date, "DD MMM YYYY (ddd)") || "-"}
            </div>

            <div
              className="text-muted"
              style={{ fontSize: "10px", lineHeight: 1.2 }}
            >
              {getMomentLocaleTimeFormat(
                session?.ses_start_time,
                "HH:mm:ss",
                userSetting.locale
              ) ?? "-"}{" "}
              -{" "}
              {getMomentLocaleTimeFormat(
                session?.ses_end_time,
                "HH:mm:ss",
                userSetting.locale
              ) ?? "-"}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface PreNextPlanHeaderBaseProps {
  title: string
  enrolment: any
  countTotalCoins: (enrolment: any) => React.ReactNode
  countTotalActivity: (enrolment: any) => React.ReactNode
  actions?: React.ReactNode
}

const PreNextPlanHeaderBase = ({
  title,
  enrolment,
  countTotalCoins,
  countTotalActivity,
  actions,
}: PreNextPlanHeaderBaseProps) => {
  return (
    <div className="section-divider flex-shrink-0">
      <div className="border-bottom pb-2 mb-2">
        <div className="d-flex justify-content-between align-items-center gap-2">
          <h6 className="mb-0 text-dark fw-700" style={{ fontSize: "13px" }}>{title}</h6>
          <div className="d-flex justify-content-end flex-shrink-0">
            {actions}
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between list-unstyled mb-0">
        <div className="d-flex align-items-center fw-400 text-dark" style={{ fontSize: "13px" }}>
          {(enrolment.tbl_session_attendance.attendance_status != 6 || enrolment?.tbl_session_attendance?.is_paused == 1) ? (
            <>
              <img className="me-10" width={20} src={awicon} alt="" />
              {countTotalCoins(enrolment) || null}
            </>
          ) : null}
        </div>

        <div className="d-flex align-items-center fw-400 text-dark" style={{ fontSize: "13px" }}></div>
      </div>

      {(enrolment.tbl_session_attendance.attendance_status != 6 || enrolment?.tbl_session_attendance?.is_paused == 1)
        ? countTotalActivity(enrolment) || null
        : null}
    </div>
  )
}

interface PreNextLessonHeaderProps {
  enrolment: any
  selectedActivityKeys?: Array<string | number>
  rolloverToNextSessionOptions: ContextMenuOption<{
    studentKey: string | number
  }>[]
  onFetchMultipleUpcomingSessionData: (
    studentKey: string | number,
    enrolmentSubject: any
  ) => Promise<void> | void
  selectAllChecked: boolean
  onSelectAllChange: (activities: PlanLessonActivity[]) => void
  countLessonTotalCoins: (enrolment: any) => React.ReactNode
  countLessonTotalActivity: (enrolment: any) => React.ReactNode
}

const PreNextLessonHeader = ({
  enrolment,
  selectedActivityKeys,
  rolloverToNextSessionOptions,
  onFetchMultipleUpcomingSessionData,
  selectAllChecked,
  onSelectAllChange,
  countLessonTotalCoins,
  countLessonTotalActivity,
}: PreNextLessonHeaderProps) => {
  const actions = (
    <>
      {selectedActivityKeys && selectedActivityKeys.length > 0 ? (
        <>
          <ContextMenuDropdown
            name="To upcoming session"
            context={{ studentKey: enrolment.fk_stu_key }}
            options={rolloverToNextSessionOptions as any}
          />
          <button
            className="btn bg-danger text-white me-3 font-size-12"
            onClick={async () => {
              await onFetchMultipleUpcomingSessionData(
                enrolment.fk_stu_key,
                enrolment.tbl_enrolment_subject
              )
            }}
          >
            Roll over
          </button>
        </>
      ) : null}

      {enrolment.tbl_student &&
        enrolment.tbl_student.Lesson &&
        enrolment.tbl_student.Lesson.LessonActivities.length > 0 ? (
        <input
          type="checkbox"
          checked={selectAllChecked}
          onChange={() =>
            onSelectAllChange(enrolment.tbl_student.Lesson.LessonActivities)
          }
        />
      ) : null}
    </>
  )

  return (
    <PreNextPlanHeaderBase
      title="Lesson Plan"
      enrolment={enrolment}
      countTotalCoins={countLessonTotalCoins}
      countTotalActivity={countLessonTotalActivity}
      actions={actions}
    />
  )
}

interface PreNextHomeworkHeaderProps {
  enrolment: any
  countHomeworkTotalCoins: (enrolment: any) => React.ReactNode
  countHomeworkTotalActivity: (enrolment: any) => React.ReactNode
}

const PreNextHomeworkHeader = ({
  enrolment,
  countHomeworkTotalCoins,
  countHomeworkTotalActivity,
}: PreNextHomeworkHeaderProps) => {
  return (
    <PreNextPlanHeaderBase
      title="Homework"
      enrolment={enrolment}
      countTotalCoins={countHomeworkTotalCoins}
      countTotalActivity={countHomeworkTotalActivity}
    />
  )
}

interface PreNextActivitiesListProps {
  enrolments: any[]
  isHomework: boolean
  sessionData: any
}

const PreNextActivitiesList = ({
  enrolments,
  isHomework,
  sessionData,
}: PreNextActivitiesListProps) => {
  const activityItems = enrolments.flatMap((enrolment, index) => {
    if (!hasRenderableEnrolment(enrolment)) {
      return []
    }

    return getEnrolmentActivities(enrolment, isHomework).map(
      (activity: PlanLessonActivity, index1: number) => ({
        key: `${isHomework ? "hw" : "lesson"}-${activity.pk_lesson_activity_key
          }-${index}-${index1}`,
        activity,
        enrolment,
        index: index1,
      })
    )
  })

  const hasItems = activityItems.length > 0

  return (
    <div
      className="events rm-heignt border-bottom1 mt-0 d-flex flex-column"
      style={{
        minHeight: 0,
      }}
    >
      {hasItems ? (
        activityItems.map(item => (
          <SessionActivityCard
            key={item.key}
            activity={item.activity}
            isHomework={isHomework}
            sessionData={sessionData}
            index={item.index}
            enrolment={item.enrolment}
          />
        ))
      ) : null}
    </div>
  )
}

export const PlanLessonHeaderSection = ({
  isCreateMode,
  sessionStudentData,
  upcommingSessionArrayOption,
  sessionKey,
  studentKey,
  studentArrayOption,
  switchCurrentFlexi,
  onToggleCurrentFlexi,
  onHandleStudentIsleaderboard,
  onTutorNoteClick,
  subjectDisplayName,
  onBack,
  onSessionChange,
  onStudentChange,
  onAutoAssignClick,
  onMoveIncompleteClick,
  onChildSnapshotClick,
  Offsymbol,
  OnSymbol,
}: PlanLessonHeaderSectionProps) => {
  const selectedStudent = studentArrayOption?.find(
    (option: SelectOption) => option.value == studentKey
  )

  const selectedSession = upcommingSessionArrayOption?.find(
    (option: SelectOption) => option.value == sessionKey
  )

  const tblStudent =
    sessionStudentData?.session?.tbl_session_time?.tbl_student_enrolment?.[0]
      ?.tbl_student

  const actionButtonStyle: React.CSSProperties = {
    border: "1px solid #4f68b6",
    color: "#314b9a",
    background: "#fff",
    fontSize: "12px",
    fontWeight: 600,
    padding: "6px 12px",
    borderRadius: "4px",
    whiteSpace: "nowrap",
    lineHeight: 1.15,
    flex: "0 0 auto",
  }

  return (
    <div className="bg-white" style={{ marginTop: 0, marginBottom: "2px" }}>
      <div
        className="pd-10"
        style={{ paddingTop: 0, paddingBottom: 0 }}
      >
        <div className="d-flex align-items-center justify-content-between gap-2 flex-nowrap"
          style={{ minWidth: 0 }}>
          <div className="d-flex align-items-center gap-2 flex-nowrap"
            style={{ flex: "0 1 auto", minWidth: 0, overflow: "hidden" }}>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ width: "24px", flex: "0 0 24px" }}
            >
              <div className="short-ic" onClick={onBack}>
                <img className="bc-bg2" src={chevleft} alt="back" />
              </div>
            </div>

            <div style={{ minWidth: "110px", maxWidth: "110px" }}>
              <h6
                className="mb-0 text-dark fw-700"
                style={{ fontSize: "11px", lineHeight: "1.15" }}
              >
                {sessionStudentData?.session?.tbl_session_time?.sest_name ||
                  "-"}
              </h6>
              <p
                className="mb-0 fs-sans-s text-muted"
                style={{ fontSize: "10px", lineHeight: "1.15" }}
              >
                {sessionStudentData?.session?.tbl_session_time?.sest_type ||
                  "-"}
              </p>
            </div>

            <div
              className="d-flex align-items-center gap-2"
              style={{ minWidth: "190px" }}
            >
              <div>
                <HoverCard.Root>
                  <HoverCard.Trigger asChild>
                    <img
                      width={36}
                      height={36}
                      className="rounded-circle obj-fit-cover"
                      src={st2}
                      alt="notes"
                      onClick={() =>
                        onTutorNoteClick?.(
                          sessionStudentData?.session?.tutor_notes || ""
                        )
                      }
                    />
                  </HoverCard.Trigger>

                  <HoverCard.Portal>
                    <HoverCard.Content
                      className="HoverCardContent"
                      sideOffset={5}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 7,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 15,
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between">
                            <h5>Notes</h5>
                            <div
                              onClick={() =>
                                onTutorNoteClick?.(
                                  sessionStudentData?.session?.tutor_notes || ""
                                )
                              }
                            >
                              <i className="fas fa-pen"></i>
                            </div>
                          </div>

                          <div className="Text">
                            {sessionStudentData?.session?.tutor_notes ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html:
                                    sessionStudentData?.session?.tutor_notes.replace(
                                      /\n/g,
                                      "<br />"
                                    ) ?? "No notes available",
                                }}
                              />
                            ) : null}
                          </div>
                        </div>

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 15,
                          }}
                        >
                          <h5>Centre Notes</h5>
                          <div className="Text">
                            {sessionStudentData?.session?.centre_notes ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html:
                                    sessionStudentData?.session?.centre_notes,
                                }}
                              />
                            ) : (
                              <span className="text-muted">
                                No centre notes
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <HoverCard.Arrow className="HoverCardArrow" />
                    </HoverCard.Content>
                  </HoverCard.Portal>
                </HoverCard.Root>
              </div>

              <div style={{ minWidth: "150px" }}>
                <Select
                  options={studentArrayOption as any}
                  className="cs-style"
                  classNamePrefix="select2-selection-bold"
                  id="studentOption"
                  name="studentOption"
                  value={selectedStudent as any}
                  onChange={(selectedOption: any) => {
                    onStudentChange?.(
                      selectedOption?.value,
                      selectedOption?.subject_id as string | number | undefined
                    )
                  }}
                  styles={compactStudentSelectStyles as any}
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  menuPosition="fixed"
                />
              </div>
            </div>

            <div style={{ minWidth: "80px", maxWidth: "80px" }}>
              <h6
                className="mb-0 text-dark fw-700"
                style={{ fontSize: "11px", lineHeight: "1.15" }}
              >
                {selectedStudent?.level_name || "-"}
              </h6>
              <p
                className="mb-0 fs-sans-s"
                style={{ fontSize: "10px", lineHeight: "1.15" }}
              >
                {subjectDisplayName || "-"}
              </p>
            </div>

            <div style={{ minWidth: "135px" }}>
              <Select
                options={upcommingSessionArrayOption as any}
                classNamePrefix="select2-selection-bold"
                id="sessioOption"
                name="sessioOption"
                value={selectedSession as any}
                onChange={(selectedOption: any) => {
                  onSessionChange?.(selectedOption?.value)
                }}
                className="cs-style"
                styles={compactSessionSelectStyles as any}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
              />
            </div>

            <div className="d-flex align-items-center gap-2 flex-nowrap">
              <div>
                <p className="mb-0" style={{ fontSize: "9px" }}>
                  Move to flexi
                </p>
                <div className="switch-wrapper">
                  <div className="sw-child-md">
                    <Switch
                      uncheckedIcon={<Offsymbol />}
                      checkedIcon={<OnSymbol />}
                      onColor="#ec4561"
                      onChange={onToggleCurrentFlexi}
                      checked={switchCurrentFlexi}
                    />
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-0" style={{ fontSize: "9px" }}>
                  Leaderboard
                </p>
                <div className="switch-wrapper">
                  <div className="sw-child-md">
                    <Switch
                      uncheckedIcon={<Offsymbol />}
                      checkedIcon={<OnSymbol />}
                      onColor="#ec4561"
                      onChange={(data: boolean) => {
                        if (tblStudent) {
                          onHandleStudentIsleaderboard?.(
                            data,
                            tblStudent.pk_student_key
                          )
                        } else {
                          console.log("student key not found")
                        }
                      }}
                      checked={tblStudent?.is_leaderboard == 1 ? true : false}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ flex: "1 1 auto", textAlign: "center", padding: "0 12px" }}>
            <h6
              className="mb-0 text-dark fw-700"
              style={{ fontSize: "16px", lineHeight: "1.2", whiteSpace: "nowrap" }}
            >
              {isCreateMode ? "Create" : "Edit"} Session Plan
            </h6>
          </div>

          <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap flex-shrink-0">
            {onChildSnapshotClick ? (
              <button
                type="button"
                className="ai-lp-snapshot-btn"
                onClick={onChildSnapshotClick}
              >
                <SnapshotIcon />
                Child snapshot
              </button>
            ) : null}

            <button
              type="button"
              style={actionButtonStyle}
              onClick={onAutoAssignClick}
            >
              Auto Assign Next Activity
            </button>

            <button
              type="button"
              style={actionButtonStyle}
              onClick={onMoveIncompleteClick}
            >
              Move Incomplete Activity
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const PreNextSessionSection = ({
  embedded,
  preNextType,
  currentPreviousSessioncount,
  previousSessioncount,
  triggerPrevious,
  triggerNextPrevious,
  triggerNext,
  sessionPreNextData,
  switchFlexi,
  onToggleFlexi,
  Offsymbol,
  OnSymbol,
  selectedActivityKeys,
  rolloverToNextSessionOptions,
  onFetchMultipleUpcomingSessionData,
  selectAllChecked,
  onSelectAllChange,
  countLessonTotalCoins,
  countLessonTotalActivity,
  countHomeworkTotalCoins,
  countHomeworkTotalActivity,
}: PreNextSessionSectionProps) => {
  const enrolments =
    sessionPreNextData?.session?.tbl_session_time?.tbl_student_enrolment || []

  const renderableEnrolments = enrolments.filter(hasRenderableEnrolment)

  return (
    <div
      className={
        embedded ? undefined : "kb-child ehp-w past-session-column p-0 me-10 border-0"
      }
      style={embedded ? undefined : { minHeight: 0, height: "100%" }}
    >
      <PreNextSessionHeader
        preNextType={preNextType}
        currentPreviousSessioncount={currentPreviousSessioncount}
        previousSessioncount={previousSessioncount}
        triggerPrevious={triggerPrevious}
        triggerNextPrevious={triggerNextPrevious}
        triggerNext={triggerNext}
        sessionPreNextData={sessionPreNextData}
        switchFlexi={switchFlexi}
        onToggleFlexi={onToggleFlexi}
        Offsymbol={Offsymbol}
        OnSymbol={OnSymbol}
      />

      <div className="lesson-plan-wrapper bg-white px-2 pt-0 pb-2">
        {renderableEnrolments.map((enrolment: any, index: number) => (
          <PreNextLessonHeader
            key={`lesson-header-${index}`}
            enrolment={enrolment}
            selectedActivityKeys={selectedActivityKeys}
            rolloverToNextSessionOptions={rolloverToNextSessionOptions}
            onFetchMultipleUpcomingSessionData={
              onFetchMultipleUpcomingSessionData
            }
            selectAllChecked={selectAllChecked}
            onSelectAllChange={onSelectAllChange}
            countLessonTotalCoins={countLessonTotalCoins}
            countLessonTotalActivity={countLessonTotalActivity}
          />
        ))}

        <PreNextActivitiesList
          enrolments={renderableEnrolments}
          isHomework={false}
          sessionData={sessionPreNextData}
        />
      </div>

      <div className="homework-plan-wrapper bg-white px-2 pt-0 pb-2 mt-2">
        {renderableEnrolments.map((enrolment: any, index: number) => (
          <PreNextHomeworkHeader
            key={`homework-header-${index}`}
            enrolment={enrolment}
            countHomeworkTotalCoins={countHomeworkTotalCoins}
            countHomeworkTotalActivity={countHomeworkTotalActivity}
          />
        ))}

        <PreNextActivitiesList
          enrolments={renderableEnrolments}
          isHomework={true}
          sessionData={sessionPreNextData}
        />
      </div>
    </div>
  )
}

interface PlanningSectionHeaderContentProps {
  title: string
  isHomework: boolean
  noteValue: string
  onNoteChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  totalCoins: React.ReactNode
  sessionData: any
  orderChecked: boolean
  onToggleOrder: (checked: boolean) => void
  onAddActivityTypeClick: (event: any) => void
  onAddOneTimeTask: (event: any) => void
  navSelectedIndex: number
  activityCount: number
  onPrev: () => void
  onNext: () => void
}

const PlanningSectionHeaderContent = ({
  title,
  isHomework,
  noteValue,
  onNoteChange,
  totalCoins,
  sessionData,
  orderChecked,
  onToggleOrder,
  onAddActivityTypeClick,
  onAddOneTimeTask,
  navSelectedIndex,
  activityCount,
  onPrev,
  onNext,
}: PlanningSectionHeaderContentProps) => {
  const controller = usePlanLessonLocal()
  const session = sessionData?.session
  const sessionTime = session?.tbl_session_time
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false)

  const handleAddNewTask = () => {
    controller.setnewactivityhomework(isHomework)
    controller.tog_large()
  }

  return (
    <>
      <Modal
        isOpen={isMessageModalOpen}
        toggle={() => setIsMessageModalOpen(false)}
        centered
      >
        <ModalHeader
          className="mt-0"
          toggle={() => setIsMessageModalOpen(false)}
        >
          {title} - Message From Tutor
        </ModalHeader>
        <ModalBody>
          <div className="comment-section mt-3">
            <label className="font-size-15" htmlFor={`${title}-message`}>
              Message From Tutor
            </label>
            <textarea
              id={`${title}-message`}
              className="form-control"
              placeholder="Add your message here"
              value={noteValue || ""}
              onChange={onNoteChange}
              rows={8}
            />
          </div>

          <div className="text-end mt-3">
            <div className="btn-group">
              <button
                type="button"
                className="btn bg-danger text-white me-3 font-size-12"
                onClick={() => setIsMessageModalOpen(false)}
              >
                Done
              </button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <div className="ss-details border-bottom1 mb-2 pb-2 flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5
            className="mb-0 text-dark fw-700"
            style={{ fontSize: "12px", lineHeight: 1.1 }}
          >
            {title}
          </h5>

          <div className="plan-lesson-header-actions">
            <button
              type="button"
              className="btn btn-sm plan-lesson-header-action-btn plan-lesson-header-action-btn--message"
              onClick={() => setIsMessageModalOpen(true)}
            >
              Message From Tutor
            </button>

            <div className="plan-lesson-header-actions__add-group">
              <button
                type="button"
                className="btn btn-sm plan-lesson-header-action-btn plan-lesson-header-action-btn--add"
                onClick={handleAddNewTask}
              >
                Add New Task
              </button>

              <button
                type="button"
                className="btn btn-sm plan-lesson-header-action-btn plan-lesson-header-action-btn--add"
                onClick={onAddOneTimeTask}
              >
                Add One-time Task
              </button>

              <button
                type="button"
                className="btn btn-sm plan-lesson-header-action-btn plan-lesson-header-action-btn--add"
              >
                <Link
                  to="/session-newactivity"
                  onClick={onAddActivityTypeClick}
                  className="plan-lesson-header-action-link"
                >
                  Add New Activity
                </Link>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="lesson-plan-wrapper bg-white px-2 py-1 flex-shrink-0">
        <div className="section-divider">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <div className="d-flex align-items-center gap-4 flex-wrap">
              <div
                className="d-flex align-items-center fw-400 text-dark"
                style={{ fontSize: "10px", lineHeight: 1.05 }}
              >
                <img className="me-05" width={16} src={awicon} alt="" />
                {totalCoins || "-"}
              </div>

              <div>
                {(
                  <span
                    className="text-dark fw-700"
                    style={{ fontSize: "10px", lineHeight: 1.1 }}
                  >
                    {activityCount} {activityCount > 1 ? "Activities" : "Activity"}
                  </span>
                )}
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 ms-auto flex-wrap">
              <div className="d-flex align-items-center gap-2">
                <span
                  className="text-dark fw-700"
                  style={{ fontSize: "10px", lineHeight: 1 }}
                >
                  {!orderChecked ? "Random" : "Sequential"}
                </span>

                <div style={planningHeaderMiniSwitchWrapStyle}>
                  <Switch
                    id={
                      title === "Homework" ? "homeActivity" : "lessonActivity"
                    }
                    onColor="#a2a2a2"
                    onChange={onToggleOrder}
                    checked={orderChecked}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    height={18}
                    width={34}
                  />
                </div>

                {!orderChecked ? (
                  <i
                    className="mdi mdi-shuffle-variant"
                    style={{ fontSize: "15px", lineHeight: 1 }}
                  ></i>
                ) : (
                  <i
                    className="mdi mdi-sort-ascending"
                    style={{ fontSize: "15px", lineHeight: 1 }}
                  ></i>
                )}
              </div>

              <div className="d-flex align-items-center gap-1">
                <SessionNavButton
                  show={navSelectedIndex !== 0}
                  onClick={onPrev}
                  iconClassName="mdi mdi-chevron-left"
                />

                <SessionNavButton
                  show={
                    activityCount > 0 && navSelectedIndex !== activityCount - 1
                  }
                  onClick={onNext}
                  iconClassName="mdi mdi-chevron-right"
                />
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="pe-2" style={{ minWidth: 0 }}>
              <div
                className="text-dark fw-700"
                style={{ fontSize: "clamp(10px, 0.72vw, 12px)", lineHeight: 1.15 }}
              >
                {sessionTime?.sest_name || "-"}
              </div>
              <div
                className="text-muted"
                style={{ fontSize: "10px", lineHeight: 1.1 }}
              >
                {sessionTime?.sest_type || "-"}
              </div>
            </div>

            <div className="text-end ms-auto">
              <div
                className="text-dark fw-700"
                style={{ fontSize: "11px", lineHeight: 1.1 }}
              >
                {formatSessionDate(session?.ses_date, "DD MMM YYYY (ddd)") ||
                  "-"}
              </div>

              <div
                className="text-muted"
                style={{ fontSize: "10px", lineHeight: 1.1 }}
              >
                {getMomentLocaleTimeFormat(
                  session?.ses_start_time,
                  "HH:mm:ss",
                  userSetting.locale
                ) ?? "-"}{" "}
                -{" "}
                {getMomentLocaleTimeFormat(
                  session?.ses_end_time,
                  "HH:mm:ss",
                  userSetting.locale
                ) ?? "-"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

interface PlanningActivitiesListProps {
  activities: PlanLessonActivity[]
  droppableId: string
  onDragEnd: (result: any) => void
  dragDisabled?: boolean
  isHomework: boolean
  sessionData: any
}

const PlanningActivitiesList = ({
  activities,
  droppableId,
  onDragEnd,
  dragDisabled,
  isHomework,
  sessionData,
}: PlanningActivitiesListProps) => {
  return (
    <div
      className={`events border-bottom1 mt-0 ${isHomework ? "" : "lesson"
        }`}
      style={{
        minHeight: 0,
        height: "100%",
        flex: "1 1 0",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={droppableId}>
          {(provided: any) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{ minHeight: activities.length > 0 ? 0 : "100%", paddingBottom: "80px" }}
            >
              {activities.length > 0 ? (
                activities.map((activity, index) => (
                  <PlanningActivityCard
                    key={`${droppableId}-${activity.pk_lesson_activity_key}`}
                    activity={activity}
                    dragDisabled={dragDisabled}
                    isHomework={isHomework}
                    sessionData={sessionData}
                    index={index}
                  />
                ))
              ) : null}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

interface PlanningSectionInternalProps extends PlanningSectionProps {
  title: string
  isHomework: boolean
}

const PlanningSection = ({
  title,
  noteValue,
  onNoteChange,
  totalCoins,
  sessionData,
  orderChecked,
  onToggleOrder,
  onAddActivityTypeClick,
  onAddOneTimeTask,
  navSelectedIndex,
  activityCount,
  onPrev,
  onNext,
  activities,
  droppableId,
  onDragEnd,
  dragDisabled,
  isHomework,
}: PlanningSectionInternalProps) => {
  return (
    <div
      className={`kb-child ehp-w bg-white px-2 pt-0 pb-2 ${isHomework ? "" : "me-10"
        } border-0 state-bg d-flex flex-column`}
      style={{ minHeight: 0, height: "100%", overflow: "hidden" }}
    >
      <PlanningSectionHeaderContent
        title={title}
        isHomework={isHomework}
        noteValue={noteValue}
        onNoteChange={onNoteChange}
        totalCoins={totalCoins}
        sessionData={sessionData}
        orderChecked={orderChecked}
        onToggleOrder={onToggleOrder}
        onAddActivityTypeClick={onAddActivityTypeClick}
        onAddOneTimeTask={onAddOneTimeTask}
        navSelectedIndex={navSelectedIndex}
        activityCount={activityCount}
        onPrev={onPrev}
        onNext={onNext}
      />

      <div
        className="lesson-plan-wrapper bg-white p-2 d-flex flex-column flex-grow-1"
        style={{ minHeight: 0, overflow: "hidden" }}
      >
        <PlanningActivitiesList
          activities={activities}
          droppableId={droppableId}
          onDragEnd={onDragEnd}
          dragDisabled={dragDisabled}
          isHomework={isHomework}
          sessionData={sessionData}
        />
      </div>
    </div>
  )
}

export const LessonPlanningSection = (props: PlanningSectionProps) => (
  <PlanningSection {...props} title="Lesson Plan" isHomework={false} />
)

export const HomeworkPlanningSection = (props: PlanningSectionProps) => (
  <PlanningSection {...props} title="Homework" isHomework={true} />
)
